/**
 * API Client for Admin Panel
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

class ApiClient {
  constructor() {
    this.baseUrl = API_URL;
    this.token = null;
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('admin_token', token);
  }

  loadAuth() {
    const stored = localStorage.getItem('admin_token');
    if (stored) {
      this.token = stored;
    }
  }

  clearAuth() {
    this.token = null;
    localStorage.removeItem('admin_token');
  }

  async request(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const config = {
      ...options,
      headers,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);

    if (response.status === 401 || response.status === 429) {
      // 401 = Invalid/expired token, 429 = Account locked
      const data = await response.json();
      this.clearAuth();
      // Kick the UI back to the login screen — clearAuth() alone doesn't
      // update React state, so notify anyone listening (useAuth)
      window.dispatchEvent(new CustomEvent('admin-auth-expired'));
      throw new Error(data.error || 'Authentication required');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }

    return data;
  }

  // Auth
  async login(username, password) {
    // Call the new JWT login endpoint
    const response = await fetch(`${this.baseUrl}/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    if (!data.success || !data.token) {
      throw new Error('Invalid login response');
    }

    // Store JWT token
    this.setToken(data.token);

    return data;
  }

  logout() {
    this.clearAuth();
  }

  // Inventory
  async getInventory() {
    return this.request('/admin/inventory');
  }

  async updateInventory(variantGid, totalUnits, reason) {
    return this.request(`/admin/inventory/${encodeURIComponent(variantGid)}`, {
      method: 'PUT',
      body: JSON.stringify({ totalUnits, reason }),
    });
  }

  async getInventoryHistory(variantGid) {
    return this.request(`/admin/inventory/${encodeURIComponent(variantGid)}/history`);
  }

  // Inventory Schedule
  async getInventorySchedule(variantGid) {
    return this.request(`/admin/inventory/${encodeURIComponent(variantGid)}/schedule`);
  }

  async addInventorySchedule(variantGid, effectiveDate, totalUnits, note) {
    return this.request(`/admin/inventory/${encodeURIComponent(variantGid)}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ effectiveDate, totalUnits, note }),
    });
  }

  async updateInventorySchedule(scheduleId, totalUnits, note) {
    return this.request(`/admin/inventory/schedule/${scheduleId}`, {
      method: 'PUT',
      body: JSON.stringify({ totalUnits, note }),
    });
  }

  async deleteInventorySchedule(scheduleId) {
    return this.request(`/admin/inventory/schedule/${scheduleId}`, {
      method: 'DELETE',
    });
  }

  // Color Inventory (Farb-Pool)
  async getColorInventory() {
    return this.request('/admin/color-inventory');
  }

  async updateColorInventory(color, totalUnits, reason) {
    return this.request(`/admin/color-inventory/${color}`, {
      method: 'PUT',
      body: JSON.stringify({ totalUnits, reason }),
    });
  }

  async getColorInventoryHistory(color) {
    return this.request(`/admin/color-inventory/${color}/history`);
  }

  async getColorInventorySchedule(color) {
    return this.request(`/admin/color-inventory/${color}/schedule`);
  }

  async addColorInventorySchedule(color, effectiveDate, totalUnits, note) {
    return this.request(`/admin/color-inventory/${color}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ effectiveDate, totalUnits, note }),
    });
  }

  async updateColorInventorySchedule(scheduleId, totalUnits, note) {
    return this.request(`/admin/color-inventory/schedule/${scheduleId}`, {
      method: 'PUT',
      body: JSON.stringify({ totalUnits, note }),
    });
  }

  async deleteColorInventorySchedule(scheduleId) {
    return this.request(`/admin/color-inventory/schedule/${scheduleId}`, {
      method: 'DELETE',
    });
  }

  // Bookings
  async getBookings() {
    return this.request('/admin/bookings');
  }

  async createBooking(bookingData) {
    return this.request('/admin/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async cancelOrder(id, reason = 'Customer request') {
    return this.request(`/admin/orders/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async refundOrder(id, refundData = {}) {
    return this.request(`/admin/orders/${id}/refund`, {
      method: 'POST',
      body: JSON.stringify(refundData),
    });
  }

  // Products (for manual booking creation)
  async getProducts() {
    return this.request('/products');
  }

  // Availability checking (for manual booking creation)
  async checkAvailability(variantGid, date) {
    return this.request(`/availability/check?variantId=${encodeURIComponent(variantGid)}&eventDate=${date}`);
  }

  // Blocked Dates
  async getBlockedDates() {
    return this.request('/admin/blocked-dates');
  }

  async addBlockedDate(startDate, endDate, reason) {
    return this.request('/admin/blocked-dates', {
      method: 'POST',
      body: JSON.stringify({ startDate, endDate, reason }),
    });
  }

  async deleteBlockedDate(id) {
    return this.request(`/admin/blocked-dates/${id}`, {
      method: 'DELETE',
    });
  }

  // Customers (NEW)
  async getCustomers(page = 1, limit = 20, search = '', tags = []) {
    const params = new URLSearchParams({ page, limit, search, tags: tags.join(',') });
    return this.request(`/admin/customers?${params}`);
  }

  async getCustomer(id) {
    return this.request(`/admin/customers/${id}`);
  }

  async createCustomer(customerData) {
    return this.request('/admin/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
  }

  async updateCustomer(id, customerData) {
    return this.request(`/admin/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  }

  async updateCustomerData(email, customerData) {
    return this.request(`/admin/customers/by-email/${encodeURIComponent(email)}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  }

  async deleteCustomer(id) {
    return this.request(`/admin/customers/${id}`, {
      method: 'DELETE',
    });
  }

  async getCustomerNotes(customerId) {
    return this.request(`/admin/customers/${customerId}/notes`);
  }

  async addCustomerNote(customerId, content) {
    return this.request(`/admin/customers/${customerId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async deleteCustomerNote(customerId, noteId) {
    return this.request(`/admin/customers/${customerId}/notes/${noteId}`, {
      method: 'DELETE',
    });
  }

  async getCustomerOrders(customerId) {
    return this.request(`/admin/customers/${customerId}/orders`);
  }

  // Orders (NEW)
  async getOrders(page = 1, limit = 20, filters = {}) {
    const params = new URLSearchParams({ page, limit, ...filters });
    return this.request(`/admin/orders?${params}`);
  }

  async getOrder(id) {
    return this.request(`/admin/orders/${id}`);
  }

  async cancelOrder(id, reason) {
    return this.request(`/admin/orders/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // Dashboard (NEW)
  async getDashboardStats() {
    return this.request('/admin/dashboard/stats');
  }

  async getDashboardTimeline() {
    return this.request('/admin/dashboard/timeline');
  }

  async getTodayEvents() {
    return this.request('/admin/dashboard/today-events');
  }

  // Backups
  async getBackups() {
    return this.request('/admin/backups');
  }

  async createBackup() {
    return this.request('/admin/backups/create', {
      method: 'POST',
    });
  }

  // Migration
  async migrateInventory() {
    return this.request('/admin/migrate-inventory', {
      method: 'POST',
    });
  }

  // Shipping Management (NEW)
  async getUpcomingBookings() {
    return this.request('/admin/bookings/upcoming');
  }

  async getCriticalBookings() {
    return this.request('/admin/bookings/critical');
  }

  async createShippingLabel(bookingId) {
    return this.request(`/admin/bookings/${bookingId}/shipping/create-label`, {
      method: 'POST',
    });
  }

  async markAsShipped(bookingId) {
    return this.request(`/admin/bookings/${bookingId}/shipping/mark-shipped`, {
      method: 'POST',
    });
  }

  async getShippingHistory(bookingId) {
    return this.request(`/admin/bookings/${bookingId}/shipping/history`);
  }

  async updateShippingStatus(bookingId, status) {
    return this.request(`/admin/bookings/${bookingId}/shipping/status`, {
      method: 'PATCH',
      body: JSON.stringify({ shipping_status: status }),
    });
  }

  async runOverdueCheck() {
    return this.request('/admin/shipping/run-overdue-check', {
      method: 'POST',
    });
  }

  async cleanupCancelledBookings() {
    return this.request('/admin/bookings/cleanup', {
      method: 'DELETE',
    });
  }

  async deleteAllBookingsAndOrders() {
    return this.request('/admin/bookings/delete-all', {
      method: 'DELETE',
    });
  }

  // Active Promo (gekoppelter Streichpreis + automatisch angewendeter Rabattcode)
  async getActivePromo() {
    return this.request('/admin/active-promo');
  }

  async updateActivePromo(isActive, code) {
    return this.request('/admin/active-promo', {
      method: 'PUT',
      body: JSON.stringify({ isActive, code }),
    });
  }

  // Discount Codes
  async getDiscountCodes(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.type) params.append('type', filters.type);
    if (filters.search) params.append('search', filters.search);

    const query = params.toString();
    return this.request(`/admin/discount-codes${query ? `?${query}` : ''}`);
  }

  async getDiscountCode(id) {
    return this.request(`/admin/discount-codes/${id}`);
  }

  async createDiscountCode(data) {
    return this.request('/admin/discount-codes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDiscountCode(id, data) {
    return this.request(`/admin/discount-codes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDiscountCode(id) {
    return this.request(`/admin/discount-codes/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleDiscountCode(id, active) {
    return this.request(`/admin/discount-codes/${id}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    });
  }

  async getDiscountAnalytics(id, dateRange = {}) {
    const params = new URLSearchParams();
    if (dateRange.start) params.append('start_date', dateRange.start);
    if (dateRange.end) params.append('end_date', dateRange.end);

    const query = params.toString();
    return this.request(`/admin/discount-codes/${id}/analytics${query ? `?${query}` : ''}`);
  }

  async getDiscountUsageHistory(id, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);

    const query = params.toString();
    return this.request(`/admin/discount-codes/${id}/usage-history${query ? `?${query}` : ''}`);
  }

  async syncDiscountCodes() {
    return this.request('/admin/discount-codes/sync', {
      method: 'POST',
    });
  }

  // Generic GET/POST methods for flexibility
  async get(endpoint) {
    return this.request(endpoint);
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Newsletter Marketing
  async getNewsletterConfig() {
    return this.request('/admin/newsletter/config');
  }

  // Instant Discount Marketing
  async getInstantDiscountConfig() {
    return this.request('/admin/instant-discount/config');
  }

  async saveInstantDiscountConfig(data) {
    return this.request('/admin/instant-discount/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async saveNewsletterConfig(data) {
    return this.request('/admin/newsletter/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getNewsletterStats() {
    return this.request('/admin/newsletter/stats');
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }
}

export const api = new ApiClient();
export default api;
